/**
 * server-unavailable — single source of truth for the user-facing
 * message when the gateway / a worker is unreachable, returns a 5xx,
 * or a non-JSON body (e.g. a Cloudflare HTML error page).
 *
 * Why this util exists:
 *   1. PRIVACY/UX — we must never bubble `TypeError: Failed to fetch`,
 *      `api.uyyamadilu.com`, status codes like `502`, or any other
 *      infrastructure detail into the UI. The shopper / operator should
 *      see one consistent line and know who to ask.
 *   2. SINGLE SOURCE — every viewmodel + util that touches `fetch`
 *      routes its error path through here so the message is identical
 *      everywhere and changing the wording is a one-line edit.
 *
 * Two helpers cover the two failure shapes you actually see in the
 * browser:
 *   - `runFetch(fn)` — wraps an `await fetch(…)` call (or
 *     `authedFetch(…)`); rethrows a sentinel Error with the generic
 *     message when the network call itself fails (DNS, CORS, offline,
 *     CF edge unreachable). On success, returns the Response unchanged.
 *   - `genericServerError()` — the canonical `{ field: null, message }`
 *     pair you can return from any catch / fallback branch.
 *
 * Public API surface is intentionally tiny — most callers only need
 * `SERVER_UNAVAILABLE_MESSAGE` + the wrapper.
 */

export const SERVER_UNAVAILABLE_MESSAGE =
  'Server not available, please contact Uyyamadilu team.'

export interface GenericError {
  readonly field: null
  readonly message: typeof SERVER_UNAVAILABLE_MESSAGE
}

export function genericServerError(): GenericError {
  return { field: null, message: SERVER_UNAVAILABLE_MESSAGE }
}

/**
 * Did this thrown value come from `fetch()` failing to reach the
 * network? Browsers throw `TypeError` (with messages like
 * "Failed to fetch", "NetworkError when attempting to fetch resource",
 * "Load failed" on Safari) — every flavour is the same UX-wise.
 * AbortError is intentionally NOT treated as server-unavailable (it's
 * a deliberate cancel, e.g. usePaginatedList cleanup).
 */
export function isNetworkError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return false
  return err instanceof TypeError
}

/**
 * Run a fetch (or authedFetch). On a network-level failure, throw a
 * fresh Error carrying the generic message — the caller's existing
 * `catch (e) { setError(e.message) }` paths then surface the right
 * string without knowing anything about the underlying failure.
 *
 * Pass-through on success — the Response is untouched and the normal
 * `!res.ok` / status-based branching continues to run.
 */
export async function runFetch(fn: () => Promise<Response>): Promise<Response> {
  try {
    return await fn()
  } catch (err) {
    if (isNetworkError(err)) {
      throw new Error(SERVER_UNAVAILABLE_MESSAGE)
    }
    throw err
  }
}
